<template>
  <span :class="containerClass" :style="style">
    <img v-bind="$attrs" :style="imageStyle" :class="imageClass" @error="onError" v-if="isImage" />
    <object
      v-bind="$attrs"
      :data="`${$attrs.src}#scrollbar=0&view=FitH`"
      :style="imageStyle"
      :class="imageClass"
      @error="onError"
      v-else
    />
    <div class="p-image-preview-indicator" v-if="preview" @click.stop="onImageClick">
      <slot name="indicator">
        <i class="p-image-preview-icon pi pi-eye"></i>
      </slot>
    </div>
    <Portal>
      <div :ref="maskRef" :class="maskClass" v-if="maskVisible" @click="onMaskClick">
        <div class="p-image-toolbar">
          <button class="p-image-action p-link" @click="rotateRight" type="button" v-if="isImage">
            <i class="pi pi-refresh"></i>
          </button>
          <button class="p-image-action p-link" @click="rotateLeft" type="button" v-if="isImage">
            <i class="pi pi-undo"></i>
          </button>
          <button
            class="p-image-action p-link"
            @click="zoomOut"
            type="button"
            :disabled="zoomDisabled"
            v-if="isImage"
          >
            <i class="pi pi-search-minus"></i>
          </button>
          <button
            class="p-image-action p-link"
            @click="zoomIn"
            type="button"
            :disabled="zoomDisabled"
            v-if="isImage"
          >
            <i class="pi pi-search-plus"></i>
          </button>
          <button class="p-image-action p-link" type="button" @click="hidePreview">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <transition
          name="p-image-preview"
          @before-enter="onBeforeEnter"
          @enter="onEnter"
          @leave="onLeave"
          @before-leave="onBeforeLeave"
          @after-leave="onAfterLeave"
        >
          <div v-if="previewVisible">
            <img
              :src="$attrs.src"
              class="p-image-preview"
              :style="imagePreviewStyle"
              @click="onPreviewImageClick"
              v-if="isImage"
            />
            <object
              :data="`${$attrs.src}#page=1&toolbar=0&view=FitH`"
              class="p-image-preview p-image-preview-object"
              :style="imagePreviewStyle"
              @click="onPreviewImageClick"
              v-else
            />
          </div>
        </transition>
      </div>
    </Portal>
  </span>
</template>

<script>
import { addClass } from "@primeuix/utils/dom";
import { ZIndex } from '@primeuix/utils/zindex';
import Portal from "primevue/portal";

export default {
  name: "Image",
  inheritAttrs: false,
  emits: ["show", "hide", "error"],
  props: {
    preview: {
      type: Boolean,
      default: false,
    },
    class: null,
    style: null,
    imageStyle: null,
    imageClass: null,
    isImage: {
      type: Boolean,
      default: true,
    },
  },
  mask: null,
  data() {
    return {
      maskVisible: false,
      previewVisible: false,
      rotate: 0,
      scale: 1,
    };
  },
  beforeUnmount() {
    if (this.mask) {
      ZIndex.clear(this.container);
    }
  },
  methods: {
    maskRef(el) {
      this.mask = el;
    },
    toolbarRef(el) {
      this.toolbarRef = el;
    },
    onImageClick() {
      if (this.preview) {
        this.maskVisible = true;
        setTimeout(() => {
          this.previewVisible = true;
        }, 25);
      }
    },
    onPreviewImageClick() {
      this.previewClick = true;
    },
    onMaskClick() {
      if (!this.previewClick) {
        this.previewVisible = false;
        this.rotate = 0;
        this.scale = 1;
      }

      this.previewClick = false;
    },
    onError() {
      this.$emit("error");
    },
    rotateRight() {
      this.rotate += 90;
      this.previewClick = true;
    },
    rotateLeft() {
      this.rotate -= 90;
      this.previewClick = true;
    },
    zoomIn() {
      this.scale = this.scale + 0.1;
      this.previewClick = true;
    },
    zoomOut() {
      this.scale = this.scale - 0.1;
      this.previewClick = true;
    },
    onBeforeEnter() {
      ZIndex.set("modal", this.mask, this.$primevue.config.zIndex.modal);
    },
    onEnter() {
      this.$emit("show");
    },
    onBeforeLeave() {
      addClass(this.mask, "p-overlay-mask-leave");
    },
    onLeave() {
      this.$emit("hide");
    },
    onAfterLeave(el) {
      ZIndex.clear(el);
      this.maskVisible = false;
    },
  },
  computed: {
    containerClass() {
      return [
        "p-image p-component",
        this.class,
        {
          "p-image-preview-container": this.preview,
        },
      ];
    },
    maskClass() {
      return ["p-image-mask p-overlay-mask p-overlay-mask-enter"];
    },
    rotateClass() {
      return "p-image-preview-rotate-" + this.rotate;
    },
    imagePreviewStyle() {
      return { transform: "rotate(" + this.rotate + "deg) scale(" + this.scale + ")" };
    },
    zoomDisabled() {
      return this.scale <= 0.5 || this.scale >= 1.5;
    },
  },
  components: {
    Portal: Portal,
  },
};
</script>

<style lang="scss">
.p-image-mask {
  display: flex;
  align-items: center;
  justify-content: center;
}

.p-image-preview-container {
  position: relative;
  display: inline-block;
  max-height: 7.5rem;
  overflow: hidden;

  & > object {
    /* To remove scrollbar from thumbnail */
    height: 200vh;
  }
}

.p-image-preview-indicator {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.p-image-preview-icon {
  font-size: 1.5rem;
}

.p-image-preview-container:hover > .p-image-preview-indicator {
  opacity: 1;
  cursor: pointer;
}

.p-image-preview-container > img {
  cursor: pointer;
}

.p-image-toolbar {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
}

.p-image-action.p-link {
  display: flex;
  justify-content: center;
  align-items: center;
}

.p-image-preview {
  transition: transform 0.15s;
  max-width: 100vw;
  max-height: 100vh;
}
.p-image-preview-object {
  width: 80vw;
  height: 100vh;
}

.p-image-preview-enter-active {
  transition: all 150ms cubic-bezier(0, 0, 0.2, 1);
}
.p-image-preview-leave-active {
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
}
.p-image-preview-enter-from,
.p-image-preview-leave-to {
  opacity: 0;
  transform: scale(0.7);
}
</style>
